import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BG_IMG =
  "https://images.unsplash.com/photo-1753024678727-aee2dc39e2dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxzd2lzcyUyMGdyYXBoaWMlMjBkZXNpZ24lMjBwb3N0ZXIlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODc1OTI1MjJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user && user.role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      nav(res.user.role === "admin" ? "/admin" : "/", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F9F9F8]">
      {/* Left: image */}
      <div
        className="hidden md:block relative overflow-hidden"
        style={{
          backgroundImage: `url(${BG_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white flex items-center justify-center">
              <Clock size={20} className="text-[#0033CC]" />
            </div>
            <span className="font-display font-black text-xl">TIMEBLOCK</span>
          </div>
          <div>
            <div className="font-display font-black text-5xl leading-none mb-3">
              Time,
              <br />
              accounted for.
            </div>
            <div className="text-sm text-white/80 max-w-md">
              Precision timesheets for teams that bill by the hour. Enter, submit,
              approve — nothing more, nothing less.
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-8 h-8 bg-[#0033CC] flex items-center justify-center">
              <Clock size={18} className="text-white" />
            </div>
            <span className="font-display font-black text-lg">TIMEBLOCK</span>
          </div>

          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Sign in
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl mb-8 leading-none">
            Log the hours,
            <br />
            close the week.
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-neutral-600">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm h-11"
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-neutral-600">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm h-11"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white font-medium transition-colors"
            >
              {loading ? "Signing in…" : (
                <span className="inline-flex items-center gap-2">
                  Sign in <ArrowRight size={16} />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-[#E5E5E5] text-xs text-neutral-500 leading-relaxed">
            Accounts are created by the workspace admin. Contact your administrator
            if you need access.
          </div>
        </div>
      </div>
    </div>
  );
}
